/**
 * FilterPanel — layout component for the filter section of the Bulk
 * Add Course Section modal.
 *
 * This file composes the filter UI from smaller parts:
 *   - A "Filter By" header with a back-arrow
 *   - An empty-state prompt when no conditions exist yet
 *   - A scrollable list of FilterRow components (one per condition)
 *   - Action buttons (Add Condition / Filter)
 *
 * The scrollable container caps at ~3 visible rows (210 px) to keep the
 * modal height predictable; additional rows scroll vertically.  This
 * prevents the filter section from pushing the DataGrid off-screen when
 * the user adds many conditions.
 *
 * `filterRows` is the CDG header metadata array — each entry describes
 * a filterable field (name, display label, data type).  It is passed
 * through to every FilterRow so the field dropdown and type-aware
 * operator list can be populated.
 */
import React from 'react'
import { Box, Typography, IconButton, Divider } from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import EmptyFilterBox from './EmptyFilterBox'
import FilterRow from './FilterRow'
import FilterActions from './FilterActions'
import { type UseFormReturn, type FieldValues, type FieldArrayWithId } from 'react-hook-form'

interface FilterPanelProps {
  fields: Array<FieldArrayWithId<FieldValues, 'filters', 'id'>>
  methods: UseFormReturn<any>
  handleBack: () => void
  handleAddCondition: () => void
  handleRemoveFilter: (index: number) => void
  handleFilter: () => void
  /** CDG header metadata — filterable fields with their data types. */
  filterRows: any[]
  filterRowsLoading: boolean
  filterRowsError: any
}

const FilterPanel: React.FC<FilterPanelProps> = ({
  fields,
  methods,
  handleBack,
  handleAddCondition,
  handleRemoveFilter,
  handleFilter,
  filterRows,
  filterRowsLoading,
  filterRowsError
}) => (
  <Box>
    {/* Fixed Header - "Filter By" with back arrow */}
    <Box display="flex" alignItems="center" mb={3}>
      <IconButton onClick={handleBack} sx={{ padding: 0, mr: 1 }}>
        <ArrowBackIcon />
      </IconButton>
      <Typography variant="h6" fontWeight={600}>
        Filter By
      </Typography>
    </Box>

    {fields.length === 0
      ? (
      <EmptyFilterBox onAddCondition={handleAddCondition} />
        )
      : (
      <>
        {/* Scrollable Filter Rows Container - Shows max 3 rows, scrolls from 4th */}
        <Box
          sx={{
            maxHeight: fields.length > 3 ? '210px' : 'auto',
            overflowY: fields.length > 3 ? 'auto' : 'visible',
            overflowX: 'hidden',
            mb: 2,
            pr: fields.length > 3 ? 1 : 0,
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: '#f1f1f1',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px',
              '&:hover': {
                backgroundColor: '#555'
              }
            }
          }}
        >
          {fields.map((item, index) => (
            <FilterRow
              key={item.id}
              item={item}
              index={index}
              methods={methods}
              handleRemoveFilter={handleRemoveFilter}
              filterRows={filterRows}
              filterRowsLoading={filterRowsLoading}
              filterRowsError={filterRowsError}
            />
          ))}
        </Box>

        {/* Fixed Action Buttons - Always visible */}
        <FilterActions
          onAddCondition={handleAddCondition}
          onFilter={handleFilter}
        />
      </>
        )}
  </Box>
)

export default FilterPanel
